import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { motion, useInView, useAnimation } from "framer-motion";
import CountUp from "react-countup";
import Marquee from "react-fast-marquee";
import { 
  Menu, X, ChevronRight, MapPin, Phone, Mail, 
  Wheat, Factory, Car, Gem, Package, Shield, 
  Award, Globe, Users, Clock, Building2, Send,
  ExternalLink, CheckCircle2, Upload
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "./components/ui/select";
import { Toaster, toast } from "sonner";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Stat Counter Component
const StatCounter = ({ end, suffix = "", label, duration = 2 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <div ref={ref} className="text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="stat-number text-4xl md:text-5xl lg:text-6xl text-white mb-2">
        {isInView ? (
          <CountUp end={end} duration={duration} suffix={suffix} />
        ) : (
          "0"
        )}
      </div>
      <div className="text-blue-200 text-sm md:text-base uppercase tracking-wider">{label}</div>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "About", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Why Us", href: "#why-us" },
    { name: "Clients", href: "#clients" },
    { name: "Offices", href: "#offices" },
    { name: "Careers", href: "#careers" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? "glass shadow-lg py-3" : "bg-transparent py-5"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <a href="#" className="flex items-center gap-2" data-testid="logo">
            <div className={scrolled ? "text-[#0A2540]" : "text-white"}>
              <div className="font-bold text-xl leading-tight">OMIC India</div>
              <div className="text-xs opacity-80">Quality Assurance</div>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`nav-link text-sm font-medium transition-colors ${
                  scrolled ? "text-[#0A2540]" : "text-white"
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </a>
            ))}
            <a href="#contact">
              <Button 
                className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
                data-testid="nav-enquire-btn"
              >
                Enquire Now
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 ${scrolled ? "text-[#0A2540]" : "text-white"}`}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden mt-4 py-4 bg-white rounded-xl shadow-xl"
            data-testid="mobile-menu"
          >
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="block px-4 py-3 text-[#0A2540] hover:bg-gray-50"
              >
                {link.name}
              </a>
            ))}
            <div className="px-4 pt-3">
              <a href="#contact" onClick={() => setIsOpen(false)}>
                <Button className="w-full bg-[#0066FF] hover:bg-[#0052CC] text-white">
                  Enquire Now
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      className="relative min-h-screen flex items-center"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1707860139049-5432cfad9fbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxzaGlwcGluZyUyMGxvZ2lzdGljcyUyMHBvcnQlMjBzaGlwJTIwY2FyZ298ZW58MHx8fHwxNzc1MjgzMjM5fDA&ixlib=rb-4.1.0&q=85')"
        }}
      />
      <div className="hero-gradient absolute inset-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="text-[#0066FF] text-sm uppercase tracking-[0.2em] mb-4 font-medium">
            Since 1957 • OMIC Japan Affiliate
          </div>
          <h1 className="text-display font-bold text-white mb-6" data-testid="hero-title">
            Your Trusted Partner in{" "}
            <span className="text-[#0066FF]">Quality Assurance</span>
          </h1>
          <p className="text-xl text-gray-200 mb-8 leading-relaxed max-w-2xl">
            Independent, third-party Testing, Inspection and Certification (TIC) services 
            to traders and manufacturers worldwide. Making supply chains safer and more efficient.
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="#services">
              <Button 
                size="lg" 
                className="btn-primary text-lg px-8"
                data-testid="hero-services-btn"
              >
                Our Services <ChevronRight className="ml-2" size={20} />
              </Button>
            </a>
            <a href="#contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="text-white border-white hover:bg-white hover:text-[#0A2540] text-lg px-8"
                data-testid="hero-contact-btn"
              >
                Contact Us
              </Button>
            </a>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12"
          data-testid="hero-stats"
        >
          <StatCounter end={68} suffix="+" label="Years Experience" />
          <StatCounter end={5} label="Offices in India" />
          <StatCounter end={50} suffix="+" label="Global Clients" />
          <StatCounter end={1000} suffix="+" label="Inspections/Year" />
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <AnimatedSection>
            <div className="text-[#0066FF] text-sm uppercase tracking-[0.2em] mb-4 font-medium">
              About Us
            </div>
            <h2 className="text-heading font-bold text-[#0A2540] mb-6">
              Trusted Expertise Since 1957
            </h2>
            <div className="space-y-4 text-[#64748B] leading-relaxed">
              <p>
                Overseas Merchandise Inspection Co. (India) Pvt. Ltd. is the Indian affiliate 
                of Overseas Merchandise Inspection Co. Ltd., Tokyo, Japan (OMIC Japan) – a leading 
                provider of independent, third-party Testing, Inspection and Certification (TIC) 
                services to traders and manufacturers worldwide.
              </p>
              <p>
                Our operations in India began in 1957, when OMIC Japan set up a branch office 
                in Kolkata, followed by offices at other locations along India&apos;s Eastern coastline. 
                In 1977, we became an incorporated Indian company, with shareholding participation by Indian entrepreneurs.
              </p>
              <p>
                Today, our footprint across India includes five main offices, located at 
                Kolkata (Head Office), Mumbai, Chennai, Delhi, and Vizag, along with smaller 
                offices at several locations across the country.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-[#0066FF]/10 flex items-center justify-center">
                  <Globe className="text-[#0066FF]" size={24} />
                </div>
                <div>
                  <div className="font-semibold text-[#0A2540]">Global Network</div>
                  <div className="text-sm text-[#64748B]">Worldwide Reach</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-[#0066FF]/10 flex items-center justify-center">
                  <Shield className="text-[#0066FF]" size={24} />
                </div>
                <div>
                  <div className="font-semibold text-[#0A2540]">TIC Council Member</div>
                  <div className="text-sm text-[#64748B]">Brussels, Belgium</div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBoYW5kc2hha2UlMjBtZWV0aW5nfGVufDB8fHx8MTc3NTI4MzI1MXww&ixlib=rb-4.1.0&q=85"
                alt="Business professionals"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#0A2540] text-white p-6 rounded-xl shadow-xl">
              <div className="stat-number text-4xl text-[#0066FF]">68+</div>
              <div className="text-sm uppercase tracking-wider mt-1">Years of Excellence</div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Wheat,
      title: "Agricultural Commodities",
      description: "Rice, Wheat, Oils, Oilseeds, Tea, Coffee, Pulses, and more. Comprehensive sampling, quality inspection, and shipment supervision.",
      image: "https://images.unsplash.com/photo-1626349442320-e5ebd1b99655?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxhZ3JpY3VsdHVyYWwlMjB3aGVhdCUyMGZpZWxkJTIwZmFybXxlbnwwfHx8fDE3NzUyODMyMzl8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: Factory,
      title: "Plant & Machinery",
      description: "Quality assurance for processing machinery, steel plants, material handling, rotary drives, and electrical equipment.",
      image: "https://images.pexels.com/photos/12951623/pexels-photo-12951623.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    },
    {
      icon: Car,
      title: "Automobiles & Components",
      description: "Inspection services for passenger cars, motorcycles, and auto components for leading manufacturers.",
      image: "https://images.unsplash.com/photo-1769971361854-9e0927a2d8dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxhdXRvbW9iaWxlJTIwcGFydHMlMjBnZWFycyUyMG1ldGFsfGVufDB8fHx8MTc3NTI4MzI1MXww&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: Gem,
      title: "Minerals & Metals",
      description: "Coal, Bauxite, Iron Ore, Manganese, Chrome Ore, and various metals inspection and shipment supervision.",
      image: "https://images.unsplash.com/photo-1645505298235-dd222c05b7ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxtaW5lcmFscyUyMG1ldGFscyUyMHJhdyUyMG9yZXxlbnwwfHx8fDE3NzUyODMyNTF8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: Package,
      title: "General Items",
      description: "Chemicals, paper, textiles, pharma intermediates, tyres, and various industrial supplies inspection.",
      image: "https://images.unsplash.com/photo-1703977883249-d959f2b0c1ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxzaGlwcGluZyUyMGxvZ2lzdGljcyUyMHBvcnQlMjBzaGlwJTIwY2FyZ298ZW58MHx8fHwxNzc1MjgzMjM5fDA&ixlib=rb-4.1.0&q=85"
    }
  ];

  return (
    <section id="services" className="py-24 bg-[#F8FAFC]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <div className="text-[#0066FF] text-sm uppercase tracking-[0.2em] mb-4 font-medium">
            Our Services
          </div>
          <h2 className="text-heading font-bold text-[#0A2540] mb-4">
            Comprehensive Inspection Services
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto">
            We ensure goods traded meet agreed specifications in quality and quantity, 
            complying with local and international regulations.
          </p>
        </AnimatedSection>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="bento-grid"
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={fadeInUp}
              className={`service-card bg-white rounded-2xl overflow-hidden border border-[#E2E8F0] ${
                index === 0 ? "lg:col-span-2 lg:row-span-2" : ""
              }`}
              data-testid={`service-card-${index}`}
            >
              <div className={`relative ${index === 0 ? "h-64 lg:h-80" : "h-48"}`}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/80 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-3 text-white">
                    <div className="w-10 h-10 rounded-lg bg-[#0066FF] flex items-center justify-center">
                      <service.icon size={20} />
                    </div>
                    <h3 className="font-bold text-lg">{service.title}</h3>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-[#64748B]">{service.description}</p>
                <a 
                  href="#contact" 
                  className="inline-flex items-center gap-2 text-[#0066FF] font-medium mt-4 hover:gap-3 transition-all"
                >
                  Learn More <ChevronRight size={16} />
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Why OMIC Section
const WhyOMICSection = () => {
  const reasons = [
    {
      icon: Globe,
      title: "Global Network",
      description: "Access to OMIC's worldwide network including world-class laboratories in Tokyo, Bangkok, Melbourne, and Portland."
    },
    {
      icon: Award,
      title: "Trusted Expertise",
      description: "Trusted by international aid agencies like World Food Programme (WFP) and ODA-Japan for relief material inspection."
    },
    {
      icon: Shield,
      title: "ISO Certified",
      description: "ISO 9001:2015 certified, ensuring consistent quality and adherence to international standards."
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Highly qualified engineers and inspectors with decades of experience across diverse product categories."
    }
  ];

  return (
    <section id="why-us" className="py-24 section-dark" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <div className="text-[#0066FF] text-sm uppercase tracking-[0.2em] mb-4 font-medium">
            Why Choose Us
          </div>
          <h2 className="text-heading font-bold text-white mb-4">
            Why OMIC India?
          </h2>
          <p className="text-blue-200 max-w-2xl mx-auto">
            OMIC group and its affiliates are trusted throughout global supply chains 
            for their robust, transparent inspection processes.
          </p>
        </AnimatedSection>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {reasons.map((reason, index) => (
            <motion.div
              key={reason.title}
              variants={fadeInUp}
              className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
              data-testid={`why-card-${index}`}
            >
              <div className="w-14 h-14 rounded-xl bg-[#0066FF] flex items-center justify-center mb-4">
                <reason.icon className="text-white" size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{reason.title}</h3>
              <p className="text-blue-200 text-sm leading-relaxed">{reason.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Accreditations Section
const AccreditationsSection = () => {
  const accreditations = [
    { 
      name: "TIC Council Member", 
      subtitle: "Brussels, Belgium",
      link: "https://omic.wdemo.in/7eeq9agd_20250321_OMIC_TIC_Council_Membership_Certificate_2025.pdf"
      // link: "https://customer-assets.emergentagent.com/job_merchandise-inspect/artifacts/7eeq9agd_20250321_OMIC_TIC_Council_Membership_Certificate_2025.pdf"
    },
    { 
      name: "DGFT Registered", 
      subtitle: "India",
      link: null
    },
    { 
      name: "ISO 9001:2015", 
      subtitle: "IRCLASS Certified",
      link: "https://omic.wdemo.in/9cdczcx1_OMIC_ISO_Certificate-9001-2015-corrected.pdf"
      // link: "https://customer-assets.emergentagent.com/job_merchandise-inspect/artifacts/9cdczcx1_OMIC_ISO_Certificate-9001-2015-corrected.pdf"
    }
  ];

  const qualityPolicyLink = "https://omic.wdemo.in/magvzvlu_QUALITY_POLICY-FINAL.PDF";
  // const qualityPolicyLink = "https://customer-assets.emergentagent.com/job_merchandise-inspect/artifacts/magvzvlu_QUALITY_POLICY-FINAL.PDF";

  return (
    <section className="py-16 bg-white" data-testid="accreditations-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-12">
          <div className="text-[#0066FF] text-sm uppercase tracking-[0.2em] mb-4 font-medium">
            Accreditations
          </div>
          <h2 className="text-2xl font-bold text-[#0A2540]">
            Certified & Recognized
          </h2>
        </AnimatedSection>

        <div className="flex flex-wrap justify-center gap-6 mb-8">
          {accreditations.map((acc, index) => (
            <motion.div
              key={acc.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`accreditation-badge ${acc.link ? 'cursor-pointer hover:border-[#0066FF]' : ''}`}
              data-testid={`accreditation-${index}`}
              onClick={() => acc.link && window.open(acc.link, '_blank')}
            >
              <CheckCircle2 className="text-[#0066FF]" size={24} />
              <div>
                <div className="font-semibold text-[#0A2540] flex items-center gap-2">
                  {acc.name}
                  {acc.link && <ExternalLink size={14} className="text-[#0066FF]" />}
                </div>
                <div className="text-sm text-[#64748B]">{acc.subtitle}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Quality Policy */}
        <div className="flex justify-center">
          <a 
            href={qualityPolicyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A2540] text-white rounded-lg hover:bg-[#0066FF] transition-colors"
          >
            <Award size={20} />
            View Our Quality Policy
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

// Clients Section
const ClientsSection = () => {
  const clients = [
    { name: "Maruti Suzuki", logo: "https://www.carlogos.org/car-logos/maruti-suzuki-logo-1800x1800.png", fallback: "MS" },
    { name: "Larsen & Toubro", logo: null, fallback: "L&T" },
    { name: "BHEL", logo: null, fallback: "BHEL" },
    { name: "Mitsubishi Corporation", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5a/Mitsubishi_logo.svg/200px-Mitsubishi_logo.svg.png", fallback: "MC" },
    { name: "Mitsui & Co.", logo: null, fallback: "M&C" },
    { name: "Toyota Tsusho", logo: "https://www.carlogos.org/car-logos/toyota-logo-2020-europe-640.png", fallback: "TT" },
    { name: "Itochu Corporation", logo: null, fallback: "IC" },
    { name: "LT Foods Ltd.", logo: null, fallback: "LTF" },
    { name: "KRBL Ltd.", logo: null, fallback: "KRBL" },
    { name: "Sundram Fasteners", logo: null, fallback: "SF" },
    { name: "Kohinoor Foods", logo: null, fallback: "KF" },
    { name: "World Food Programme", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d4/World_Food_Programme_Logo_Simple.svg/200px-World_Food_Programme_Logo_Simple.svg.png", fallback: "WFP" },
    { name: "ASCW", logo: null, fallback: "ASCW" },
    { name: "SAEL", logo: null, fallback: "SAEL" },
    { name: "BAID AGRO", logo: null, fallback: "BA" },
    { name: "SUPPLETEK", logo: null, fallback: "ST" },
    { name: "KANEMATSU", logo: null, fallback: "KM" },
    { name: "Shree Gajanan Industries", logo: null, fallback: "SGI" },
    { name: "SUN RICE AUSTRALIA", logo: null, fallback: "SRA" },
  ];

  const [imageErrors, setImageErrors] = useState({});

  const handleImageError = (index) => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
  };

  const ClientCard = ({ client, index }) => (
    <div 
      className="marquee-item mx-4 py-5 px-6 bg-white rounded-xl border border-[#E2E8F0] shadow-sm flex items-center gap-4 min-w-[220px] h-[90px] hover:shadow-md transition-shadow"
    >
      {client.logo && !imageErrors[index] ? (
        <img 
          src={client.logo} 
          alt={client.name}
          className="h-12 w-auto max-w-[100px] object-contain"
          onError={() => handleImageError(index)}
        />
      ) : (
        <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-[#0A2540] to-[#0066FF] flex items-center justify-center text-white font-bold text-xs shrink-0">
          {client.fallback}
        </div>
      )}
      <span className="font-medium text-[#0A2540] text-sm leading-tight">{client.name}</span>
    </div>
  );

  return (
    <section id="clients" className="py-24 bg-[#F8FAFC]" data-testid="clients-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-12">
          <div className="text-[#0066FF] text-sm uppercase tracking-[0.2em] mb-4 font-medium">
            Our Clients
          </div>
          <h2 className="text-heading font-bold text-[#0A2540] mb-4">
            Trusted by Industry Leaders
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto">
            Serving prestigious domestic and international clients across diverse industries.
          </p>
        </AnimatedSection>

        <Marquee gradient gradientColor="#F8FAFC" speed={35} pauseOnHover>
          {clients.slice(0, 8).map((client, index) => (
            <ClientCard key={index} client={client} index={index} />
          ))}
        </Marquee>

        <Marquee gradient gradientColor="#F8FAFC" speed={30} direction="right" className="mt-5" pauseOnHover>
          {clients.slice(8).map((client, index) => (
            <ClientCard key={index + 8} client={client} index={index + 8} />
          ))}
        </Marquee>

        {/* Disclaimer */}
        <p className="text-center text-xs text-[#94A3B8] mt-10 max-w-3xl mx-auto leading-relaxed">
          All logos and trademarks displayed above are the property of their respective owners. 
          Their inclusion here does not imply any affiliation with or endorsement by OMIC India.
        </p>
      </div>
    </section>
  );
};

// Offices Section
const OfficesSection = () => {
  const offices = [
    {
      city: "Kolkata",
      type: "Head Office",
      address: "17, Chittaranjan Avenue (4th Floor), Kolkata – 700 072, West Bengal",
      phone: "+91-33-2225 1778 / 4402 6886 / 4005 3386"
    },
    {
      city: "Mumbai",
      type: "Branch Office",
      address: "102, Rajan House (1st Floor), Dr. Annie Besant Road, Worli, Mumbai – 400 025",
      phone: "+91-22-4970 8217 / 4451 4382"
    },
    {
      city: "Chennai",
      type: "Branch Office",
      address: "No. 487/642, Anna Salai (1st Floor), Nandanam, Chennai – 600 035",
      phone: "+91-44-2431 3346 / 2539 4525 / 2431 3343"
    },
    {
      city: "Delhi",
      type: "Branch Office",
      address: "House no. A-10, Gali no. 1, Kotla Village, Mayur Vihar, Phase-1, New Delhi – 110 091",
      phone: "+91-11-4164 7212"
    },
    {
      city: "Vizag",
      type: "Branch Office",
      address: "D. No. 23-35-2/10 (4th Floor), Flat No. B-403, Marine Enclave – II, Vizag – 530 001",
      phone: "+91-891-2561119"
    }
  ];

  return (
    <section id="offices" className="py-24 bg-white" data-testid="offices-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <div className="text-[#0066FF] text-sm uppercase tracking-[0.2em] mb-4 font-medium">
            Our Presence
          </div>
          <h2 className="text-heading font-bold text-[#0A2540] mb-4">
            Offices Across India
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto">
            Five strategically located offices ensuring comprehensive coverage across the country.
          </p>
        </AnimatedSection>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {offices.map((office, index) => (
            <motion.div
              key={office.city}
              variants={fadeInUp}
              className={`office-card p-6 rounded-xl bg-white ${
                index === 0 ? "lg:col-span-2 border-[#0066FF] border-2" : ""
              }`}
              data-testid={`office-${office.city.toLowerCase()}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-[#0A2540]">{office.city}</h3>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mt-2 ${
                    index === 0 
                      ? "bg-[#0066FF] text-white" 
                      : "bg-[#F8FAFC] text-[#64748B]"
                  }`}>
                    {office.type}
                  </span>
                </div>
                <Building2 className="text-[#0066FF]" size={24} />
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <MapPin className="text-[#64748B] flex-shrink-0 mt-1" size={16} />
                  <span className="text-[#64748B]">{office.address}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="text-[#64748B]" size={16} />
                  <span className="text-[#64748B]">{office.phone}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Careers Section
const CareersSection = () => {
  const [showJobForm, setShowJobForm] = useState(false);
  const [jobFormData, setJobFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    message: ""
  });
  const [cvFile, setCvFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      // Check file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload PDF or Word document only");
        return;
      }
      setCvFile(file);
    }
  };

  const handleJobSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', jobFormData.name);
      formData.append('email', jobFormData.email);
      formData.append('phone', jobFormData.phone);
      formData.append('position', jobFormData.position);
      formData.append('experience', jobFormData.experience);
      formData.append('message', jobFormData.message);
      if (cvFile) {
        formData.append('cv_file', cvFile);
      }

      const response = await fetch(`${backendUrl}/api/job-enquiry`, {
        method: "POST",
        body: formData
      });

      if (response.ok) {
        toast.success("Thank you for your application! We will review and contact you soon.");
        setJobFormData({
          name: "",
          email: "",
          phone: "",
          position: "",
          experience: "",
          message: ""
        });
        setCvFile(null);
        // Reset file input
        const fileInput = document.getElementById('cv-upload');
        if (fileInput) fileInput.value = '';
        setShowJobForm(false);
      } else {
        toast.error("Failed to submit application. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting job application:", error);
      toast.error("Network error. Please try again later.");
    }

    setIsSubmitting(false);
  };

  return (
    <section id="careers" className="py-24 bg-[#F8FAFC]" data-testid="careers-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <AnimatedSection>
            <div className="text-[#0066FF] text-sm uppercase tracking-[0.2em] mb-4 font-medium">
              Careers
            </div>
            <h2 className="text-heading font-bold text-[#0A2540] mb-6">
              Join Our Team
            </h2>
            <p className="text-[#64748B] mb-6 leading-relaxed">
              OMIC India offers young as well as experienced professionals the opportunity 
              to work with domestic and international clients, across industries and product categories.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-[#0066FF]" size={20} />
                <span className="text-[#0A2540]">Work with global clients</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-[#0066FF]" size={20} />
                <span className="text-[#0A2540]">Diverse industry exposure</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="text-[#0066FF]" size={20} />
                <span className="text-[#0A2540]">Professional growth opportunities</span>
              </div>
            </div>
            <div className="mt-8 p-6 bg-white rounded-xl border border-[#E2E8F0]">
              <h4 className="font-semibold text-[#0A2540] mb-2">Interested in Joining?</h4>
              <p className="text-sm text-[#64748B] mb-4">
                Submit your application below or send us your resume for future opportunities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => setShowJobForm(!showJobForm)}
                  className="bg-[#0066FF] hover:bg-[#0052CC] text-white"
                  data-testid="apply-now-btn"
                >
                  {showJobForm ? "Hide Form" : "Apply Now"}
                </Button>
                <a 
                  href="mailto:anita.vs@omicindia.com" 
                  className="inline-flex items-center gap-2 text-[#0066FF] hover:underline"
                >
                  <Mail size={16} />
                  anita.vs@omicindia.com
                </a>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            {showJobForm ? (
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-[#E2E8F0]">
                <h3 className="text-xl font-bold text-[#0A2540] mb-6">Job Application Form</h3>
                <form onSubmit={handleJobSubmit} className="space-y-5" data-testid="job-application-form">
                  <div>
                    <label className="block text-sm font-medium text-[#0A2540] mb-2">
                      Full Name *
                    </label>
                    <Input
                      required
                      placeholder="Your name"
                      value={jobFormData.name}
                      onChange={(e) => setJobFormData({ ...jobFormData, name: e.target.value })}
                      className="form-input"
                      data-testid="job-input-name"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0A2540] mb-2">
                        Email *
                      </label>
                      <Input
                        required
                        type="email"
                        placeholder="your@email.com"
                        value={jobFormData.email}
                        onChange={(e) => setJobFormData({ ...jobFormData, email: e.target.value })}
                        className="form-input"
                        data-testid="job-input-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A2540] mb-2">
                        Phone *
                      </label>
                      <Input
                        required
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={jobFormData.phone}
                        onChange={(e) => setJobFormData({ ...jobFormData, phone: e.target.value })}
                        className="form-input"
                        data-testid="job-input-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A2540] mb-2">
                      Position Applied For *
                    </label>
                    <Input
                      required
                      placeholder="e.g., Inspector, Surveyor, Lab Technician"
                      value={jobFormData.position}
                      onChange={(e) => setJobFormData({ ...jobFormData, position: e.target.value })}
                      className="form-input"
                      data-testid="job-input-position"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A2540] mb-2">
                      Years of Experience
                    </label>
                    <Select
                      value={jobFormData.experience}
                      onValueChange={(value) => setJobFormData({ ...jobFormData, experience: value })}
                    >
                      <SelectTrigger className="form-input" data-testid="job-select-experience">
                        <SelectValue placeholder="Select experience" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fresher">Fresher</SelectItem>
                        <SelectItem value="1-2">1-2 years</SelectItem>
                        <SelectItem value="3-5">3-5 years</SelectItem>
                        <SelectItem value="5-10">5-10 years</SelectItem>
                        <SelectItem value="10+">10+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A2540] mb-2">
                      Upload CV / Resume *
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        id="cv-upload"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                        required
                      />
                      <label
                        htmlFor="cv-upload"
                        className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-[#E2E8F0] rounded-lg cursor-pointer hover:border-[#0066FF] transition-colors"
                      >
                        <Upload size={20} className="text-[#64748B]" />
                        <span className="text-[#64748B]">
                          {cvFile ? cvFile.name : "Click to upload PDF or Word document (Max 5MB)"}
                        </span>
                      </label>
                      {cvFile && (
                        <button
                          type="button"
                          onClick={() => {
                            setCvFile(null);
                            const fileInput = document.getElementById('cv-upload');
                            if (fileInput) fileInput.value = '';
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                        >
                          <X size={16} className="text-[#64748B]" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A2540] mb-2">
                      Cover Note / Message
                    </label>
                    <Textarea
                      placeholder="Tell us about yourself and why you want to join OMIC India..."
                      rows={4}
                      value={jobFormData.message}
                      onChange={(e) => setJobFormData({ ...jobFormData, message: e.target.value })}
                      className="form-input"
                      data-testid="job-input-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={isSubmitting}
                    data-testid="job-submit-btn"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Application"}
                    <Send className="ml-2" size={18} />
                  </Button>
                </form>
              </div>
            ) : (
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1768661770207-9aa46d5ed526?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvcnBvcmF0ZSUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwbW9kZXJufGVufDB8fHx8MTc3NTI4MzI1MXww&ixlib=rb-4.1.0&q=85"
                  alt="Corporate office"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            )}
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // try {
    //   const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
    //   const response = await fetch(`${backendUrl}/api/enquiry`, {
    //     method: 'POST',
    //     headers: {
    //       'Content-Type': 'application/json',
    //     },
    //     body: JSON.stringify(formData),
    //   });

    //   if (response.ok) {
    //     toast.success("Thank you for your enquiry! We will contact you shortly.");
    //     setFormData({
    //       name: "",
    //       email: "",
    //       phone: "",
    //       company: "",
    //       service: "",
    //       message: ""
    //     });
    //   } else {
    //     const errorData = await response.json();
    //     toast.error("Failed to submit enquiry. Please try again.");
    //     console.error('Submission error:', errorData);
    //   }
    // } catch (error) {
    //   toast.error("Failed to submit enquiry. Please check your connection.");
    //   console.error('Network error:', error);
    // }
    console.log(formData)
    
    setIsSubmitting(false);
  };

  const contacts = [
    {
      name: "Mr. S.R Sharma",
      title: "General Manager",
      email: "sr.sharma@omicindia.com",
      phone: "+91 – 9820297739"
    },
    {
      name: "Mr. Tamal Paul",
      title: "Asst. General Manager",
      email: "t.paul@omicindia.com",
      phone: "+91-9007657631"
    },
    {
      name: "Mrs. Anita V. Sarangale",
      title: "Asst. General Manager",
      email: "anita.vs@omicindia.com",
      phone: "+91-9920750066"
    }
  ];

  return (
    <section id="contact" className="py-24 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-16">
          <div className="text-[#0066FF] text-sm uppercase tracking-[0.2em] mb-4 font-medium">
            Contact Us
          </div>
          <h2 className="text-heading font-bold text-[#0A2540] mb-4">
            Get in Touch
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto">
            To request an inspection or for more information about our services, please reach out to us.
          </p>
        </AnimatedSection>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <AnimatedSection>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A2540] mb-2">
                    Full Name *
                  </label>
                  <Input
                    required
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="form-input"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2540] mb-2">
                    Email Address *
                  </label>
                  <Input
                    required
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="form-input"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A2540] mb-2">
                    Phone Number *
                  </label>
                  <Input
                    required
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="form-input"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2540] mb-2">
                    Company Name
                  </label>
                  <Input
                    placeholder="Your company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="form-input"
                    data-testid="input-company"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0A2540] mb-2">
                  Service Required *
                </label>
                <Select
                  value={formData.service}
                  onValueChange={(value) => setFormData({ ...formData, service: value })}
                >
                  <SelectTrigger className="form-input" data-testid="select-service">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agricultural">Agricultural Commodities</SelectItem>
                    <SelectItem value="machinery">Plant & Machinery</SelectItem>
                    <SelectItem value="automobiles">Automobiles & Components</SelectItem>
                    <SelectItem value="minerals">Minerals & Metals</SelectItem>
                    <SelectItem value="general">General Items</SelectItem>
                    <SelectItem value="other">Other Services</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0A2540] mb-2">
                  Message *
                </label>
                <Textarea
                  required
                  placeholder="Tell us about your inspection requirements..."
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="form-input"
                  data-testid="input-message"
                />
              </div>

              <Button 
                type="submit" 
                size="lg" 
                className="w-full btn-primary"
                disabled={isSubmitting}
                data-testid="submit-btn"
              >
                {isSubmitting ? "Sending..." : "Send Enquiry"}
                <Send className="ml-2" size={18} />
              </Button>
            </form>
          </AnimatedSection>

          {/* Contact Info */}
          <AnimatedSection>
            <div className="space-y-6">
              <h3 className="text-xl font-bold text-[#0A2540] mb-6">Direct Contacts</h3>
              {contacts.map((contact, index) => (
                <div 
                  key={contact.name}
                  className="p-5 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0]"
                  data-testid={`contact-card-${index}`}
                >
                  <div className="font-semibold text-[#0A2540]">{contact.name}</div>
                  <div className="text-sm text-[#0066FF] mb-3">{contact.title}</div>
                  <div className="space-y-2 text-sm">
                    <a 
                      href={`mailto:${contact.email}`}
                      className="flex items-center gap-2 text-[#64748B] hover:text-[#0066FF]"
                    >
                      <Mail size={14} />
                      {contact.email}
                    </a>
                    <a 
                      href={`tel:${contact.phone.replace(/\s/g, '')}`}
                      className="flex items-center gap-2 text-[#64748B] hover:text-[#0066FF]"
                    >
                      <Phone size={14} />
                      {contact.phone}
                    </a>
                  </div>
                </div>
              ))}

              <div className="p-5 rounded-xl bg-[#0A2540] text-white">
                <h4 className="font-semibold mb-3">Head Office</h4>
                <div className="space-y-2 text-sm text-blue-200">
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="mt-1 flex-shrink-0" />
                    <span>17, Chittaranjan Avenue (4th Floor), Kolkata – 700 072, West Bengal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={14} />
                    <span>+91-33-2225 1778 / 4402 6886</span>
                  </div>
                  <a 
                    href="https://www.omicindia.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-white"
                  >
                    <ExternalLink size={14} />
                    www.omicindia.com
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0A2540] text-white py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div>
                <div className="font-bold text-xl">OMIC India</div>
                <div className="text-xs text-blue-200">Affiliated to OMIC Japan, Tokyo</div>
              </div>
            </div>
            <p className="text-blue-200 text-sm leading-relaxed max-w-md mb-4">
              Your Trusted Partner in International Quality Assurance and Inspection. 
              Providing independent, third-party Testing, Inspection and Certification services since 1957.
            </p>
            {/* Head Office Contact */}
            <div className="bg-white/10 rounded-lg p-4">
              <h4 className="font-semibold text-sm mb-2">Head Office - Kolkata</h4>
              <p className="text-blue-200 text-xs leading-relaxed">
                17, Chittaranjan Avenue (4th Floor), Kolkata – 700 072, West Bengal
              </p>
              <p className="text-blue-200 text-xs mt-1">
                Tel: +91-33-2225 1778 / 4402 6886 / 4005 3386
              </p>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm text-blue-200">
              <li><a href="#about" className="hover:text-white">About Us</a></li>
              <li><a href="#services" className="hover:text-white">Services</a></li>
              <li><a href="#clients" className="hover:text-white">Clients</a></li>
              <li><a href="#careers" className="hover:text-white">Careers</a></li>
              <li><a href="#contact" className="hover:text-white">Contact</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-sm text-blue-200">
              <li>Agricultural Commodities</li>
              <li>Plant & Machinery</li>
              <li>Automobile Components</li>
              <li>Minerals & Metals</li>
              <li>General Items</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-blue-200">
            © {currentYear} Overseas Merchandise Inspection Co. (India) Pvt. Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-sm text-blue-200">
            <span>Member of TIC Council</span>
            <span>•</span>
            <span>ISO 9001:2015 Certified</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <WhyOMICSection />
        <AccreditationsSection />
        {/* <ClientsSection /> */}
        <OfficesSection />
        <CareersSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
